import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { ProductResponse } from '@/api/bol';
import { formatExternalReferences, formatSpecialCharacteristics } from '@/utils/datagridFormatters';

interface ProductColumnsProps {
  onDelete: (product: ProductResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createProductColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: ProductColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'productIdentifier',
    headerName: 'Product Identifier',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { productIdentifier?: string }) => row.productIdentifier || '-',
  },
  {
    field: 'commodityName',
    headerName: 'Commodity Name',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { commodityName?: string }) => row.commodityName || '-',
  },
  {
    field: 'productDescription',
    headerName: 'Description',
    flex: 1.3,
    minWidth: 200,
    valueGetter: (_value: unknown, row: { productDescription?: string }) => row.productDescription || '-',
  },
  {
    field: 'hsCode',
    headerName: 'HS Code',
    flex: 0.8,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { hsCode?: string }) => row.hsCode || '-',
  },
  {
    field: 'hsType',
    headerName: 'HS Type',
    flex: 0.8,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { hsType?: string }) => row.hsType || '-',
  },
  {
    field: 'hsCommodityName',
    headerName: 'HS Commodity Name',
    flex: 1,
    minWidth: 170,
    valueGetter: (_value: unknown, row: { hsCommodityName?: string }) => row.hsCommodityName || '-',
  },
  {
    field: 'contactsCount',
    headerName: 'Contacts',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { contacts?: unknown[] }) => row.contacts?.length || 0,
  },
  {
    field: 'documentsCount',
    headerName: 'Documents',
    flex: 0.8,
    minWidth: 110,
    valueGetter: (_value: unknown, row: { documents?: unknown[] }) => row.documents?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.5,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'specialCharacteristics',
    headerName: 'Special Characteristics',
    flex: 1.4,
    minWidth: 220,
    valueGetter: (
      _value: unknown,
      row: {
        specialCharacteristics?: Array<{
          specialCharacteristicType?: string | null;
          characteristicReference?: string | null;
          stringValue?: string | null;
          numericalValue?: { value?: number; unit?: string } | null;
        }>;
      }
    ) => formatSpecialCharacteristics(row.specialCharacteristics),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/products/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as ProductResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultProductColumnVisibilityModel = {
  id: true,
  productIdentifier: true,
  commodityName: true,
  productDescription: true,
  hsCode: true,
  hsType: true,
  hsCommodityName: false,
  contactsCount: false,
  documentsCount: false,
  externalReferences: false,
  specialCharacteristics: false,
};
